import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "./useAuth";

export type MyAccessScope = {
  tier: 1 | 2 | 3;
  directAccessCompanyIds: string[];
  directAccessProductIds: string[];
  directAccessProjectIds: string[];
  parentOnlyCompanyIds: string[];
  parentOnlyProductIds: string[];
  hasNoAssignments: boolean;
};

/**
 * Fetches the authenticated user's RLAC scope so the UI can decide which
 * affordances to expose (e.g. the documents-tab rollup toggle and tree
 * button only make sense when the user has DIRECT access to the entity —
 * for Tier 3 users on parent-visibility-only entities, those tools would
 * otherwise become a side-channel rollup view).
 */
export function useMyAccessScope(): UseQueryResult<MyAccessScope> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: ["me", "access-scope", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    staleTime: 60_000,
    queryFn: () =>
      apiFetch<MyAccessScope>("/auth/me/access-scope", {
        token: accessToken,
      }),
  });
}

/**
 * Returns true when the current user has DIRECT (not parent-visibility)
 * access to the given entity. Tier 1/2 always return true. While the scope
 * is loading we optimistically return true to avoid a UI flash.
 */
export function hasDirectAccessTo(
  scope: MyAccessScope | undefined,
  entityType: "COMPANY" | "PRODUCT" | "PROJECT",
  entityId: string,
): boolean {
  if (!scope) return true;
  if (scope.tier !== 3) return true;
  if (entityType === "COMPANY")
    return scope.directAccessCompanyIds.includes(entityId);
  if (entityType === "PRODUCT")
    return scope.directAccessProductIds.includes(entityId);
  return scope.directAccessProjectIds.includes(entityId);
}
